// Settings (app flow §8), as a grouped settings page: an index down the side on desktop that follows
// the reader's place, and one stacked column on phones. Each group is a heading and a sunken panel of
// rows; every control says what it does.
// Sign-in methods (GET/DELETE /v1/me/accounts; Google and Apple linked by redirect through
// /v1/auth/link-social), devices (GET/DELETE /v1/me/sessions), how notes stand (sync), and account
// deletion as the API asks for it: {confirm: "DELETE"}, and a fresh Apple sign-in first when Apple is
// linked (403 fresh-sign-in-required), after which the deletion is retried.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { SyncIndicator } from "../../components/SyncIndicator";
import { AppleMark, GoogleMark, PROVIDER_NAME } from "../../components/ProviderButtons";
import { ApiError } from "../../data/problem";
import type { DetailVisibility, Device, ProfileDetails, SocialProvider } from "../../data/types";
import { closeSync } from "../../sync/react";
import { API_URL } from "../../api/client";
import { AvatarPicker } from "../../components/AvatarPicker";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog } from "../../components/Dialog";
import { Icon, type IconName } from "../../components/Icon";
import { Avatar } from "../../components/Avatar";
import { Badge } from "../../components/Badge";
import { Field, Input, Textarea } from "../../components/Field";
import { Surface } from "../../components/Surface";
import ui from "../../components/ui.module.css";
import { showError } from "../../components/Toast";
import { refreshSession, useAuthProviders, useBlocks, useProfile, useProfileOptions, useSession, useSource, useUnblock, useUpdateProfileDetails } from "../../data/queries";
import { useLibraryIndex, useReaderChoices } from "../../data/library";
import { getThemeChoice, setThemeChoice, type ThemeChoice } from "../../lib/theme";
import { getReadingSize, setReadingSize, type ReadingSize } from "../../lib/reading";
import { requestTour } from "../../lib/tour";
import { Button } from "../../components/Button";
import { SegmentedControl } from "../../components/Tabs";
import { useSetting } from "../../reader/settings";
import { useTitle } from "../../lib/useTitle";
import s from "./settings.module.css";

type Search = { linked?: SocialProvider; confirmDelete?: boolean; error?: string };

export const Route = createFileRoute("/_app/settings")({
  validateSearch: (q: Record<string, unknown>): Search => ({
    ...(q.linked === "google" || q.linked === "apple" ? { linked: q.linked } : {}),
    ...(q.confirmDelete ? { confirmDelete: true } : {}),
    ...(typeof q.error === "string" && q.error.length < 100 ? { error: q.error } : {}),
  }),
  component: Settings,
});

/** The groups, in page order: the side index is built from this. */
const GROUPS = [
  { id: "profile", label: "Profile", icon: "person" },
  { id: "details", label: "About you", icon: "people" },
  { id: "reading", label: "Reading", icon: "read" },
  { id: "appearance", label: "Appearance", icon: "sun" },
  { id: "sign-in", label: "Sign-in methods", icon: "lock" },
  { id: "devices", label: "Devices", icon: "devices" },
  { id: "privacy", label: "Privacy", icon: "shield" },
  { id: "notifications", label: "Notifications", icon: "bell" },
  { id: "sync", label: "Notes and sync", icon: "note" },
  { id: "help", label: "Help", icon: "spark" },
  { id: "data", label: "Your data", icon: "download" },
  { id: "delete", label: "Delete account", icon: "trash" },
] as const satisfies readonly { id: string; label: string; icon: IconName }[];
type GroupId = (typeof GROUPS)[number]["id"];

/** "Chrome on Windows" from a user agent; the app's own agent as "Bible Standard on iPhone". */
function deviceName(ua: string | null): string {
  if (!ua) return "A browser";
  if (/BibleStandard/i.test(ua)) return /Android/i.test(ua) ? "Bible Standard on Android" : /iPad/i.test(ua) ? "Bible Standard on iPad" : "Bible Standard on iPhone";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "A browser";
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : null;
  return os ? `${browser} on ${os}` : browser;
}

const when = (iso: string) => {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: days > 300 ? "numeric" : undefined });
};

/** One settings group: a serif heading, a line under it, and its rows on a sunken panel. */
function Group({ id, title, description, tone, children }: { id: GroupId; title: string; description?: ReactNode; tone?: "danger"; children: ReactNode }) {
  return (
    <section id={id} className={s.group} aria-labelledby={`${id}-head`} data-group>
      <div className={s.groupHead}>
        <h2 id={`${id}-head`} className={s.head}>
          {title}
        </h2>
        {description && <p className={s.description}>{description}</p>}
      </div>
      <Surface variant="panel" pad="none" className={tone === "danger" ? `${s.panel} ${s.danger}` : s.panel}>
        {children}
      </Surface>
    </section>
  );
}

/** A row in a panel: what it is (and a line about it) on the left, what you can do on the right. */
function Row({ icon, person = false, title, detail, action, children, stack = false }: { icon?: ReactNode; person?: boolean; title?: ReactNode; detail?: ReactNode; action?: ReactNode; children?: ReactNode; stack?: boolean }) {
  return (
    <div className={stack ? `${s.row} ${s.stack}` : s.row}>
      {(icon || title || detail) && (
        <div className={s.rowMain}>
          {icon && (
            <span className={person ? `${s.rowIcon} ${s.person}` : s.rowIcon} aria-hidden="true">
              {icon}
            </span>
          )}
          <div className={s.rowWords}>
            {title && <div className={s.rowTitle}>{title}</div>}
            {detail && <p className={s.rowDetail}>{detail}</p>}
          </div>
        </div>
      )}
      {children}
      {action && <div className={s.rowAction}>{action}</div>}
    </div>
  );
}

/** The group the reader is looking at, for the side index (the last heading above the upper third). */
function useCurrentGroup(): GroupId {
  const [current, setCurrent] = useState<GroupId>("profile");
  useEffect(() => {
    let frame = 0;
    const pick = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const sections = [...document.querySelectorAll<HTMLElement>("[data-group]")];
        const line = window.innerHeight * 0.3;
        let at = sections[0]?.id;
        for (const el of sections) if (el.getBoundingClientRect().top <= line) at = el.id;
        // At the very foot of the page the last group is current, however short it is.
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) at = sections.at(-1)?.id;
        if (at) setCurrent(at as GroupId);
      });
    };
    pick();
    window.addEventListener("scroll", pick, { passive: true });
    window.addEventListener("resize", pick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", pick);
      window.removeEventListener("resize", pick);
    };
  }, []);
  return current;
}

function Settings() {
  useTitle("Settings");
  const source = useSource();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { data: profile } = useProfile(session?.user.handle ?? "");
  const [theme, setTheme] = useState<ThemeChoice>(getThemeChoice);
  const [readingSize, setSize] = useState<ReadingSize>(getReadingSize);
  const reader = useReaderChoices();
  const [pocketStyle, setPocketStyle] = useSetting("pocketStyle");
  const { data: library } = useLibraryIndex();
  const [name, setName] = useState<string | null>(null);
  const [bio, setBio] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const fellowship = session?.fellowshipEnabled ?? true;
  const { data: blocked } = useBlocks();
  const unblock = useUnblock();
  const current = useCurrentGroup();
  /** Any refusal from the API, said in words. */
  const attempt = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      return true;
    } catch (e) {
      showError((e as Error).message);
      return false;
    }
  };
  const search = Route.useSearch();
  const [confirm, setConfirm] = useState<"signout-all" | "delete" | "apple-again" | null>(search.confirmDelete ? "delete" : null);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { data: providers } = useAuthProviders();
  const methods = useQuery({ queryKey: ["me", "accounts"], queryFn: () => source.signInMethods() });
  const devices = useQuery({ queryKey: ["me", "sessions"], queryFn: () => source.devices() });
  const [methodError, setMethodError] = useState<string | null>(search.error ? "That didn’t finish. Try linking again." : null);
  const [deviceNote, setDeviceNote] = useState<string | null>(null);
  const [linking, setLinking] = useState<SocialProvider | null>(null);
  const appleLinked = !!methods.data?.some((m) => m.provider === "apple");

  // Back from linking: say so once, and show the fresh list.
  useEffect(() => {
    if (search.linked) void methods.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.linked]);

  const link = async (provider: SocialProvider) => {
    setLinking(provider);
    setMethodError(null);
    try {
      const url = await source.startSocial(provider, { link: true, callbackURL: `${location.origin}/settings?linked=${provider}`, errorCallbackURL: `${location.origin}/settings?error=link` });
      window.location.assign(url);
    } catch (e) {
      setLinking(null);
      setMethodError((e as Error).message);
    }
  };

  const unlink = async (provider: SocialProvider) => {
    setMethodError(null);
    try {
      await source.removeSignInMethod(provider);
      await methods.refetch();
    } catch (e) {
      setMethodError(
        e instanceof ApiError && e.status === 409
          ? `${PROVIDER_NAME[provider]} is your only way to sign in, so it can’t be removed. Add another way first.`
          : (e as Error).message,
      );
    }
  };

  const signOutDevice = async (d: Device) => {
    if (!(await attempt(() => source.signOutDevice(d.id)))) return;
    setDeviceNote(`Signed out of ${deviceName(d.device)}.`);
    await devices.refetch();
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await source.deleteAccount();
    } catch (e) {
      setDeleting(false);
      // Apple is linked: Apple needs to see a fresh sign-in before the account (and its Apple link) goes.
      if (e instanceof ApiError && e.type === "fresh-sign-in-required") return setConfirm("apple-again");
      showError((e as Error).message);
      return;
    }
    await closeSync(session?.user.id && (API_URL ? session.user.id : `prototype-${session.user.id}`));
    qc.clear();
    void navigate({ to: "/", search: { deleted: true } });
  };

  const reauthApple = async () => {
    try {
      const url = await source.startSocial("apple", { callbackURL: `${location.origin}/settings?confirmDelete=1`, errorCallbackURL: `${location.origin}/settings?error=apple` });
      window.location.assign(url);
    } catch (e) {
      showError((e as Error).message);
    }
  };

  const currentName = name ?? profile?.displayName ?? "";
  const currentBio = bio ?? profile?.bio ?? "";
  const dirty = (name !== null && name !== profile?.displayName) || (bio !== null && bio !== (profile?.bio ?? ""));

  const signOut = async () => {
    const account = session?.user.id && (API_URL ? session.user.id : `prototype-${session.user.id}`);
    if (!(await attempt(() => source.signOut()))) return;
    // This browser's copy of the reader's notes goes with the session (they stay on the server).
    await closeSync(account);
    qc.clear();
    void navigate({ to: "/" });
  };

  const signOutEverywhere = async () => {
    if (!(await attempt(() => source.signOutOtherDevices()))) return;
    await signOut();
  };

  const download = async () => {
    let data: unknown;
    if (!(await attempt(async () => (data = await source.exportData())))) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `fellowship-${session?.user.handle}.json` });
    a.click();
    URL.revokeObjectURL(url);
  };

  const titleOf = (id: string) => library?.find((e) => e.id === id)?.title ?? id;
  const sortedDevices = [...(devices.data ?? [])].sort((a, b) => Number(b.current) - Number(a.current) || b.lastUsedAt.localeCompare(a.lastUsedAt));

  return (
    <div className={s.page}>
      <header className={s.pageHead}>
        <h1 className={ui.title}>Settings</h1>
        {session && (
          <p className={s.who}>
            Signed in as <strong>@{session.user.handle}</strong>
          </p>
        )}
      </header>

      <div className={s.layout}>
        <nav className={s.index} aria-label="Settings sections">
          <ul>
            {GROUPS.map((g) => (
              <li key={g.id}>
                <a href={`#${g.id}`} className={g.id === "delete" ? `${s.indexLink} ${s.indexDanger}` : s.indexLink} aria-current={current === g.id ? "location" : undefined}>
                  <Icon name={g.icon} size={16} />
                  {g.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={s.groups}>
          <Group id="profile" title="Profile" description="How friends see you: your photo, your name and a line about you.">
            {profile && (
              <Row stack>
                <AvatarPicker
                  user={profile}
                  onChange={async (url) => {
                    if (!(await attempt(() => source.setAvatar(url)))) return;
                    await refreshSession(qc, source);
                    await qc.invalidateQueries();
                  }}
                />
              </Row>
            )}
            <form
              className={s.form}
              onSubmit={async (e) => {
                e.preventDefault();
                setSaving(true);
                const ok = await attempt(() => source.updateProfile({ displayName: currentName.trim(), bio: currentBio.trim() }));
                setSaving(false);
                if (!ok) return;
                await refreshSession(qc, source);
                await qc.invalidateQueries();
                setName(null);
                setBio(null);
                setSaved(true);
              }}
            >
              <Field label="Name" id="name">
                {(p) => <Input {...p} maxLength={50} autoComplete="name" value={currentName} onChange={(e) => (setName(e.target.value), setSaved(false))} />}
              </Field>
              <Field label="About you" id="bio" hint={`Optional. Friends see this on your profile. ${160 - currentBio.length} characters left.`}>
                {(p) => <Textarea {...p} rows={3} maxLength={160} value={currentBio} onChange={(e) => (setBio(e.target.value), setSaved(false))} />}
              </Field>
              <p className={s.handle}>
                <span className={s.handleLabel}>Handle</span>
                <span className={s.handleValue}>@{session?.user.handle}</span>
                <span className={s.hint}>Handles can be changed once a month.</span>
              </p>
              <div className={s.formEnd}>
                <span role="status" className={s.savedNote}>
                  {saved && (
                    <>
                      <Icon name="check" size={16} /> Saved
                    </>
                  )}
                </span>
                <Button type="submit" variant="primary" size="sm" disabled={!currentName.trim() || !dirty} busy={saving}>
                  Save profile
                </Button>
              </div>
            </form>
          </Group>

          <Group id="details" title="About you" description="Optional. Where you are and what you believe, each with its own choice of who sees it.">
            <DetailsForm />
          </Group>

          <Group id="reading" title="Reading" description="Kept in this browser.">
            <Row title="Reading text size" detail="Scripture, posts and the readers. The interface stays the same size." stack>
              <SegmentedControl
                label="Reading text size"
                value={readingSize}
                onChange={(v) => {
                  setSize(v);
                  setReadingSize(v);
                }}
                options={[
                  { value: "regular", label: "Regular" },
                  { value: "large", label: "Large" },
                  { value: "larger", label: "Larger" },
                ]}
              />
            </Row>
            <Row title="Confession" detail="The confession you hold to. Its catechism follows it unless you pick one below. Synced with the app." stack>
              <select
                className={ui.select}
                aria-label="Confession"
                value={reader.picked.confession ?? ""}
                onChange={(e) => void reader.set({ confession: e.target.value || null })}
              >
                <option value="">None chosen</option>
                {reader.choices.confessions.map((c) => (
                  <option key={c.confession} value={c.confession}>
                    {titleOf(c.confession)}
                  </option>
                ))}
              </select>
            </Row>
            <Row title="Catechism on Home" detail={`The daily card asks one question a day. Today: ${titleOf(reader.catechism)}.`} stack>
              <select
                className={ui.select}
                aria-label="Catechism on Home"
                value={reader.picked.catechism ?? ""}
                onChange={(e) => void reader.set({ catechism: e.target.value || null })}
              >
                <option value="">Follow my confession</option>
                {reader.choices.catechisms.map((id) => (
                  <option key={id} value={id}>
                    {titleOf(id)}
                  </option>
                ))}
              </select>
            </Row>
            <Row title="Pocket" detail="Hold the Continue tile: a ring in the corner, or a dial round it. Synced with the app." stack>
              <SegmentedControl
                label="Pocket"
                value={pocketStyle}
                onChange={(v) => void setPocketStyle(v)}
                options={[
                  { value: "carousel", label: "Carousel" },
                  { value: "dial", label: "Dial" },
                ]}
              />
            </Row>
            <Row
              icon={<Icon name="bible" size={18} />}
              title="Translation"
              detail="Verses in posts are quoted from the World English Bible. The Bible reader keeps the translation you last read; change it there."
              action={<Badge tone="outline">WEB</Badge>}
            />
          </Group>

          <Group id="appearance" title="Appearance" description="Shared with biblestandard.app on this browser.">
            <Row title="Theme" detail="Match device follows your system’s light and dark." stack>
              <SegmentedControl
                label="Theme"
                value={theme}
                onChange={(t) => {
                  setTheme(t);
                  setThemeChoice(t);
                }}
                options={[
                  { value: "system", label: "Match device", icon: "device" },
                  { value: "light", label: "Light", icon: "sun" },
                  { value: "dark", label: "Dark", icon: "moon" },
                ]}
              />
            </Row>
          </Group>

          <Group id="sign-in" title="Sign-in methods" description="No passwords: an emailed code, Apple or Google.">
            {methods.isPending ? (
              <Row title="Loading…" />
            ) : methods.isError ? (
              <Row>
                <p className={ui.error} role="alert">
                  {(methods.error as Error).message}
                </p>
              </Row>
            ) : (
              <ul className={s.rows}>
                {(["email", "apple", "google"] as const).map((p) => {
                  const m = methods.data?.find((x) => x.provider === p);
                  if (p === "email")
                    return (
                      <li key={p}>
                        <Row icon={<Icon name="email" size={18} />} title="Email code" detail={m?.email ?? session?.email} action={<Badge tone={m ? "accent" : "neutral"}>{m ? "In use" : "Not in use"}</Badge>} />
                      </li>
                    );
                  const offered = providers?.[p] ?? true;
                  return (
                    <li key={p}>
                      <Row
                        icon={p === "apple" ? <AppleMark size={18} /> : <GoogleMark size={18} />}
                        title={PROVIDER_NAME[p]}
                        detail={m ? `Linked ${when(m.linkedAt)}${m.email ? `, ${m.email}` : ""}` : offered ? "Not linked" : "Not set up here yet"}
                        action={
                          m ? (
                            <Button size="sm" variant="ghost" onClick={() => void unlink(p)} aria-label={`Remove ${PROVIDER_NAME[p]}`}>
                              Remove
                            </Button>
                          ) : (
                            offered && (
                              <Button size="sm" busy={linking === p} disabled={linking === p} onClick={() => void link(p)} aria-label={`Link ${PROVIDER_NAME[p]}`}>
                                {linking === p ? "Opening…" : "Link"}
                              </Button>
                            )
                          )
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            {search.linked && !methodError && (
              <p role="status" className={s.note}>
                <Icon name="check" size={16} /> {PROVIDER_NAME[search.linked]} is linked. You can sign in with it now.
              </p>
            )}
            {methodError && (
              <p className={`${ui.error} ${s.note}`} role="alert">
                {methodError}
              </p>
            )}
          </Group>

          <Group id="devices" title="Devices" description="Everywhere your account is signed in. Signing a device out ends its session at once.">
            {devices.isPending ? (
              <Row title="Loading…" />
            ) : devices.isError ? (
              <Row>
                <p className={ui.error} role="alert">
                  {(devices.error as Error).message}
                </p>
              </Row>
            ) : (
              <ul className={s.rows} aria-label="Signed in on">
                {sortedDevices.map((d) => (
                  <li key={d.id}>
                    <Row
                      icon={<Icon name="devices" size={18} />}
                      title={
                        d.current ? (
                          <>
                            This browser <Badge tone="accent">Current</Badge>
                          </>
                        ) : (
                          deviceName(d.device)
                        )
                      }
                      detail={d.current ? `${deviceName(d.device)}, active now` : `Last used ${when(d.lastUsedAt)}, signed in ${when(d.createdAt)}`}
                      action={
                        d.current ? (
                          <Button size="sm" icon="sign-out" onClick={() => void signOut()}>
                            Sign out
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => void signOutDevice(d)} aria-label={`Sign out of ${deviceName(d.device)}`}>
                            Sign out
                          </Button>
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className={s.panelFoot}>
              <p role="status" className={s.footNote}>
                {deviceNote}
              </p>
              <div className={s.buttons}>
                {(devices.data?.length ?? 0) > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (!(await attempt(() => source.signOutOtherDevices()))) return;
                      setDeviceNote("Signed out everywhere else.");
                      await devices.refetch();
                    }}
                  >
                    Sign out of other devices
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setConfirm("signout-all")}>
                  Sign out everywhere
                </Button>
              </div>
            </div>
          </Group>

          <Group id="privacy" title="Privacy" description="Fellowship is friends only: no followers and no public feed.">
            <label className={`${s.row} ${s.toggle}`}>
              <span className={s.rowMain}>
                <span className={s.rowIcon} aria-hidden="true">
                  <Icon name="people" size={18} />
                </span>
                <span className={s.rowWords}>
                  <span className={s.rowTitle}>Fellowship is {fellowship ? "on" : "off"}</span>
                  <span className={s.rowDetail}>Off hides your profile and posts from friends. Nothing is deleted; turn it back on any time.</span>
                </span>
              </span>
              <input
                type="checkbox"
                role="switch"
                className={ui.switch}
                checked={fellowship}
                onChange={async (e) => {
                  const on = e.target.checked;
                  if (!(await attempt(() => source.setFellowship(on)))) return;
                  await refreshSession(qc, source);
                  await qc.invalidateQueries();
                }}
              />
            </label>
            <div className={s.subhead}>
              <h3 className={s.subheadTitle}>Blocked</h3>
              <p className={s.rowDetail}>People you block can’t see you or anything you share, and aren’t told.</p>
            </div>
            {blocked && blocked.length > 0 ? (
              <ul className={s.rows} aria-label="Blocked people">
                {blocked.map((u) => (
                  <li key={u.id}>
                    <Row
                      icon={<Avatar user={u} size={32} />}
                      person
                      title={u.displayName}
                      detail={`@${u.handle}`}
                      action={
                        <Button size="sm" variant="ghost" onClick={() => unblock.mutate(u.id)} aria-label={`Unblock ${u.displayName}`}>
                          Unblock
                        </Button>
                      }
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <Row detail="No one is blocked." />
            )}
            <Row
              icon={<Icon name="shield" size={18} />}
              title="What we keep"
              detail="Your date of birth is never stored, only that you are 13 or over. Fellowship runs no analytics."
              action={
                <Link to="/privacy" className={s.textLink}>
                  Privacy
                </Link>
              }
            />
          </Group>

          <Group id="notifications" title="Notifications">
            <Row
              icon={<Icon name="bell" size={18} />}
              title="Activity"
              detail="Friend requests, comments and responses to what you share wait for you in Activity. The web sends no browser notifications."
              action={
                <Link to="/notifications" className={s.textLink}>
                  Open Activity
                </Link>
              }
            />
          </Group>

          <Group id="sync" title="Notes and sync">
            <Row
              icon={<Icon name="note" size={18} />}
              title={<SyncIndicator detail />}
              detail="Highlights, notes, bookmarks, your place in each book and your shelf are kept on every device you sign in to. Notes are encrypted on our servers."
            />
          </Group>

          <Group id="help" title="Help">
            <Row
              icon={<Icon name="spark" size={18} />}
              title="Replay the walkthrough"
              detail="A short walk through Home, sharing a verse, plans and the library."
              action={
                <Button size="sm" onClick={() => requestTour()}>
                  Take the tour
                </Button>
              }
            />
            <Row
              icon={<Icon name="flag" size={18} />}
              title="The community rule"
              detail="What we ask of everyone, and how reports are handled."
              action={
                <Link to="/rules" className={s.textLink}>
                  Read it
                </Link>
              }
            />
            <Row
              icon={<Icon name="email" size={18} />}
              title="Contact"
              detail="A person reads every message."
              action={
                <Link to="/contact" className={s.textLink}>
                  Write to us
                </Link>
              }
            />
          </Group>

          <Group id="data" title="Your data">
            <Row
              icon={<Icon name="download" size={18} />}
              title="A copy of everything"
              detail="Everything Fellowship holds about you, as a JSON file: your profile, posts, comments, saves and friends."
              action={
                <Button size="sm" icon="download" onClick={() => void download()}>
                  Download my data
                </Button>
              }
            />
          </Group>

          <Group id="delete" title="Delete account" tone="danger">
            <div className={s.dangerBody}>
              <p className={s.dangerLead}>This deletes your profile, posts, comments, responses and friendships straight away.</p>
              <ul className={s.dangerList}>
                <li>Backups clear within 30 days.</li>
                <li>Purchases in the Bible Standard app are not affected.</li>
                {appleLinked && <li>Apple is linked, so Apple will ask you to sign in once more first.</li>}
              </ul>
              <div>
                <Button variant="danger" icon="trash" onClick={() => (setTyped(""), setConfirm("delete"))}>
                  Delete account
                </Button>
              </div>
            </div>
          </Group>

          <p className={s.legal}>
            <Link to="/rules">Community rule</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy">Privacy</Link>
          </p>
        </div>
      </div>

      {confirm === "signout-all" && (
        <ConfirmDialog
          title="Sign out everywhere?"
          body="Every browser and device signed in to your account will need to sign in again, including this one."
          confirm="Sign out everywhere"
          onConfirm={() => void signOutEverywhere()}
          onClose={() => setConfirm(null)}
        />
      )}
      <Dialog
        open={confirm === "delete"}
        onClose={() => {
          if (!deleting) setConfirm(null);
        }}
        size="sm"
        title="Delete your account?"
        description="This can’t be undone. Everything you’ve shared in Fellowship goes with it."
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" busy={deleting} disabled={typed.trim().toUpperCase() !== "DELETE" || deleting} onClick={() => void deleteAccount()}>
              {deleting ? "Deleting…" : "Delete for good"}
            </Button>
          </>
        }
      >
        <Field label="Type DELETE to confirm" id="confirm-delete">
          {(p) => <Input {...p} autoComplete="off" autoCapitalize="characters" spellCheck={false} value={typed} onChange={(e) => setTyped(e.target.value)} />}
        </Field>
      </Dialog>
      {confirm === "apple-again" && (
        <ConfirmDialog
          title="Confirm with Apple"
          body="Your account is linked to Apple, so Apple asks you to sign in once more before it’s deleted. You’ll come straight back here to finish."
          confirm="Continue with Apple"
          onConfirm={() => void reauthApple()}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/** FS-136: where you are and what you believe, each with who sees it. Faith details need consent once. */
function DetailsForm() {
  const { data: session } = useSession();
  const { data: options } = useProfileOptions();
  const save = useUpdateProfileDetails();
  const mine = session?.profile;
  const [draft, setDraft] = useState<ProfileDetails | null>(null);
  const [loc, setLoc] = useState<DetailVisibility | null>(null);
  const [faith, setFaith] = useState<DetailVisibility | null>(null);
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  if (!mine || !options) return null;
  const d = draft ?? mine.details;
  const locationVisibility = loc ?? mine.locationVisibility;
  const faithVisibility = faith ?? mine.faithVisibility;
  const set = (k: keyof ProfileDetails, v: string) => (setDraft({ ...d, [k]: v.trim() ? v : null }), setDone(false));
  const faithSet = !!(d.tradition || d.denomination || d.church);
  const needsConsent = faithSet && !mine.faithConsentAt;
  const dirty = draft !== null || loc !== null || faith !== null;
  const visibility = (label: string, value: DetailVisibility, onChange: (v: DetailVisibility) => void) => (
    <SegmentedControl
      size="sm"
      label={label}
      value={value}
      onChange={(v) => (onChange(v), setDone(false))}
      options={options.visibilities.map((v) => ({ value: v, label: v === "friends" ? "Friends see it" : "Only me", icon: v === "friends" ? "people" : "lock" }))}
    />
  );
  return (
    <form
      className={s.form}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          { ...d, locationVisibility, faithVisibility, ...(needsConsent ? { faithConsent: consent } : {}) },
          { onSuccess: () => (setDraft(null), setLoc(null), setFaith(null), setDone(true)) },
        );
      }}
    >
      <fieldset className={s.fieldset}>
        <legend className={s.subheadTitle}>Where you are</legend>
        <div className={s.pair}>
          <Field label="City or region" id="city" hint="Optional. Never a precise location.">
            {(p) => <Input {...p} maxLength={options.limits.city} autoComplete="address-level2" value={d.city ?? ""} onChange={(e) => set("city", e.target.value)} />}
          </Field>
          <Field label="Country" id="country" hint="Two letters, for example GB.">
            {(p) => <Input {...p} maxLength={2} autoComplete="country" value={d.country ?? ""} onChange={(e) => set("country", e.target.value.toUpperCase())} />}
          </Field>
        </div>
        {visibility("Who sees where you are", locationVisibility, setLoc)}
      </fieldset>
      <fieldset className={s.fieldset}>
        <legend className={s.subheadTitle}>Your faith</legend>
        <Field label="Tradition" id="tradition">
          {(p) => (
            <select {...p} className={ui.select} value={d.tradition ?? ""} onChange={(e) => set("tradition", e.target.value)}>
              <option value="">Not said</option>
              {options.traditions.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className={s.pair}>
          <Field label="Denomination" id="denomination" hint="In your own words.">
            {(p) => <Input {...p} maxLength={options.limits.denomination} value={d.denomination ?? ""} onChange={(e) => set("denomination", e.target.value)} />}
          </Field>
          <Field label="Church" id="church">
            {(p) => <Input {...p} maxLength={options.limits.church} value={d.church ?? ""} onChange={(e) => set("church", e.target.value)} />}
          </Field>
        </div>
        {visibility("Who sees your faith", faithVisibility, setFaith)}
        {needsConsent && (
          <label className={s.consent}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>{options.faithConsentText}</span>
          </label>
        )}
      </fieldset>
      {save.isError && (
        <p className={ui.error} role="alert">
          {(save.error as Error).message}
        </p>
      )}
      <div className={s.formEnd}>
        <span role="status" className={s.savedNote}>
          {done && (
            <>
              <Icon name="check" size={16} /> Saved
            </>
          )}
        </span>
        <Button type="submit" variant="primary" size="sm" disabled={!dirty || (needsConsent && !consent) || save.isPending} busy={save.isPending}>
          Save details
        </Button>
      </div>
    </form>
  );
}
